import {
  buildNewChatPath,
  extractChatConversationId,
  isChatExperiencePath,
  isChatNewRoute,
  isRagSpacePath,
  resolveChatIntentFromPathname,
  type ChatSessionIntent,
} from './chat-session-intent';

export const DOCUMENTS_PATH = '/documents';

/** Ordered left to right: the order drives the direction of page transitions. */
export const APP_SPACES = ['chat', 'rag', 'documents'] as const;

export type AppSpace = (typeof APP_SPACES)[number];

export type RouteTransitionDirection = 'forward' | 'backward' | 'rise';

export type DocumentsReturnTarget = {
  intent: ChatSessionIntent;
  path: string;
};

export type DocumentsLocationState = {
  returnTo?: string;
};

type ChatSpaceMemory = {
  lastIntent: ChatSessionIntent | null;
  ownerId: string;
  paths: Partial<Record<ChatSessionIntent, string>>;
};

// Per tab and per user, cleared on reload: a stale id never outlives the session.
let chatSpaceMemory: ChatSpaceMemory | null = null;

function readMemory(ownerId: string | undefined) {
  if (!ownerId || chatSpaceMemory?.ownerId !== ownerId) {
    return null;
  }
  return chatSpaceMemory;
}

export function resolveAppSpace(pathname: string): AppSpace | null {
  if (isChatExperiencePath(pathname)) {
    return resolveChatIntentFromPathname(pathname) === 'document-search' ? 'rag' : 'chat';
  }
  if (pathname === DOCUMENTS_PATH || pathname.startsWith(`${DOCUMENTS_PATH}/`)) {
    return 'documents';
  }
  return null;
}

/** Chat ↔ Recherche only: the segmented control stays put and its thumb slides. */
export function isChatSpaceSwitch(fromPathname: string, toPathname: string) {
  if (!isChatExperiencePath(fromPathname) || !isChatExperiencePath(toPathname)) {
    return false;
  }
  return resolveAppSpace(fromPathname) !== resolveAppSpace(toPathname);
}

export function resolveRouteTransitionDirection(
  from: AppSpace | null,
  to: AppSpace | null,
): RouteTransitionDirection {
  if (!from || !to || from === to) {
    return 'rise';
  }
  return APP_SPACES.indexOf(to) > APP_SPACES.indexOf(from) ? 'forward' : 'backward';
}

/**
 * Identity of the screen the outlet actually remounts. A conversation opening inside
 * Chat or Recherche, and a filter change in the document base, keep the same key.
 */
export function resolveRouteTransitionKey(pathname: string, search = '') {
  if (pathname === '/auth' || pathname.startsWith('/auth/')) {
    return '/auth';
  }
  if (pathname === '/chat' || pathname.startsWith('/chat/')) {
    return `/chat${search}`;
  }
  if (pathname === '/rag' || pathname.startsWith('/rag/')) {
    return `/rag${search}`;
  }
  if (pathname === DOCUMENTS_PATH || pathname.startsWith(`${DOCUMENTS_PATH}/`)) {
    return DOCUMENTS_PATH;
  }
  return `${pathname}${search}`;
}

/** A crossfade only when the visible screen changes, never on a silent replace. */
export function shouldAnimateRouteChange(
  from: { pathname: string; search: string },
  to: { pathname: string; search: string },
  options?: { replace?: boolean },
) {
  if (options?.replace) {
    return false;
  }
  if (resolveRouteTransitionKey(from.pathname, from.search).startsWith('/auth')
    || resolveRouteTransitionKey(to.pathname, to.search).startsWith('/auth')) {
    return false;
  }
  return resolveRouteTransitionKey(from.pathname, from.search)
    !== resolveRouteTransitionKey(to.pathname, to.search);
}

export function rememberChatSpacePath(ownerId: string | undefined, pathname: string) {
  if (!ownerId || !isChatExperiencePath(pathname)) {
    return;
  }
  const intent = resolveChatIntentFromPathname(pathname);
  const current = readMemory(ownerId);
  chatSpaceMemory = {
    lastIntent: intent,
    ownerId,
    paths: { ...current?.paths, [intent]: pathname },
  };
}

export function forgetChatConversation(conversationId: string) {
  if (!chatSpaceMemory) {
    return;
  }
  const paths = { ...chatSpaceMemory.paths };
  for (const intent of Object.keys(paths) as ChatSessionIntent[]) {
    if (extractChatConversationId(paths[intent] ?? '') === conversationId) {
      paths[intent] = buildNewChatPath(intent);
    }
  }
  chatSpaceMemory = { ...chatSpaceMemory, paths };
}

/** Where entering a space lands: its last visited route, or its new-chat route. */
export function resolveChatSpaceEntryPath(ownerId: string | undefined, intent: ChatSessionIntent) {
  return readMemory(ownerId)?.paths[intent] ?? buildNewChatPath(intent);
}

/** The page that sent the user to the documents wins; otherwise the last chat space visited. */
export function resolveDocumentsReturnTarget(
  ownerId: string | undefined,
  locationState: unknown,
): DocumentsReturnTarget {
  const returnTo = readDocumentsReturnTo(locationState);
  if (returnTo) {
    return { intent: resolveChatIntentFromPathname(returnTo), path: returnTo };
  }
  const memory = readMemory(ownerId);
  const intent = memory?.lastIntent ?? 'general';
  return { intent, path: memory?.paths[intent] ?? buildNewChatPath(intent) };
}

export function getDocumentsReturnLabel(target: DocumentsReturnTarget) {
  if (target.intent === 'document-search') {
    return 'Retour à la recherche';
  }
  return isChatNewRoute(target.path) ? 'Retour au chat' : 'Retour à la conversation';
}

/** Sidebar space while the document base is open: the space that opened it. */
export function resolveSidebarChatSpace(
  pathname: string,
  ownerId: string | undefined,
  locationState: unknown,
): 'chat' | 'rag' {
  if (pathname === DOCUMENTS_PATH || pathname.startsWith(`${DOCUMENTS_PATH}/`)) {
    return resolveDocumentsReturnTarget(ownerId, locationState).intent === 'document-search' ? 'rag' : 'chat';
  }
  return isRagSpacePath(pathname) ? 'rag' : 'chat';
}

export function buildDocumentsLocationState(pathname: string): DocumentsLocationState | undefined {
  return isChatExperiencePath(pathname) ? { returnTo: pathname } : undefined;
}

function readDocumentsReturnTo(state: unknown) {
  if (!state || typeof state !== 'object') {
    return undefined;
  }
  const returnTo = (state as Record<string, unknown>).returnTo;
  return typeof returnTo === 'string' && isChatExperiencePath(returnTo) ? returnTo : undefined;
}

export function resetChatSpaceMemoryForTests() {
  chatSpaceMemory = null;
}
